/**
 * Voice mode experiment, behind localStorage sg_copilot_voice = "1".
 *
 * Speech in: the browser's Web Speech API (Chrome). Interim words land in
 * the composer as they are recognised; a final result with a pause ends the
 * utterance and the caller sends it as a normal turn.
 *
 * Speech out: the agent's /copilot/tts (Deepgram Aura) when it is
 * configured, else speechSynthesis with Chrome's Google voice. Never reads
 * the full markdown: sentences as they stream, plus the open question.
 */
import { agentBaseUrl, ensureCopilotCredential } from "./SseAgentTransport";
import { getPrefs } from "./copilotPrefs";

export const voiceEnabled = () => {
  try {
    return localStorage.getItem("sg_copilot_voice") === "1";
  } catch (e) {
    return false;
  }
};

const Recognition =
  typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export const voiceSupported = () => Boolean(Recognition) && typeof window !== "undefined" && "speechSynthesis" in window;

/**
 * Start listening. onInterim(text) fires as words come in, onFinal(text)
 * once the utterance ends. Returns a stop() function.
 */
export const listen = ({ onInterim, onFinal, onEnd, lang = getPrefs().sttLang }) => {
  if (!Recognition) return () => {};
  const rec = new Recognition();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = true;
  rec.maxAlternatives = 1;
  let finalText = "";
  rec.onresult = event => {
    let interim = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const r = event.results[i];
      if (r.isFinal) finalText += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (onInterim) onInterim((finalText + interim).trim());
  };
  rec.onerror = () => {
    if (onEnd) onEnd();
  };
  rec.onend = () => {
    const text = finalText.trim();
    if (text && onFinal) onFinal(text);
    if (onEnd) onEnd();
  };
  try {
    rec.start();
  } catch (e) {
    if (onEnd) onEnd();
  }
  return () => {
    try {
      rec.stop();
    } catch (e) {
      /* already stopped */
    }
  };
};

let speaking = null;
// The voice list loads asynchronously in Chrome: an early getVoices() is
// empty and the fallback picked a different voice each turn. Choose once,
// when the list is in, and keep it for the life of the page.
let lockedVoice = null;
// Chrome ships neural cloud voices under "Google …"; they beat every local
// macOS/Windows voice. Same list order on Edge (falls through to Microsoft Online).
// Apple's downloadable Premium/Enhanced voices (System Settings >
// Accessibility > Spoken Content > Manage Voices) beat Google's when present.
const preferredVoices = [
  "Zoe (Premium)",
  "Ava (Premium)",
  "Evan (Premium)",
  "Samantha (Enhanced)",
  "Google US English",
  "Google UK English Female",
  "Microsoft Aria Online (Natural) - English (United States)",
  "Microsoft Jenny Online (Natural) - English (United States)",
  "Samantha",
];
const resolveVoice = () => {
  if (lockedVoice) return lockedVoice;
  const voices = window.speechSynthesis.getVoices() || [];
  if (!voices.length) return null;
  // A/B by name without a rebuild: localStorage sg_copilot_voice_name.
  let wanted = "";
  try { wanted = localStorage.getItem("sg_copilot_voice_name") || ""; } catch (e) { /* no storage */ }
  if (wanted) {
    const v = voices.find(x => x.name === wanted || x.name.startsWith(wanted));
    if (v) { lockedVoice = v; return v; }
  }
  for (const name of preferredVoices) {
    const v = voices.find(x => x.name === name);
    if (v) { lockedVoice = v; return v; }
  }
  lockedVoice = voices.find(v => /^en/i.test(v.lang)) || voices[0];
  return lockedVoice;
};
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  window.speechSynthesis.onvoiceschanged = () => resolveVoice();
  resolveVoice();
}

const speechListeners = new Set();
/** Subscribe to "speaking started/ended"; used to resume the mic after a reply. */
export const onSpeech = fn => { speechListeners.add(fn); return () => speechListeners.delete(fn); };
const notify = state => speechListeners.forEach(fn => { try { fn(state); } catch (e) { /* listener */ } });

// speechSynthesis has no SSML in Chrome: rate / pitch / volume per
// utterance is the whole instrument. One utterance per phrase, each tuned by
// what it is, queued back to back (the engine keeps order).
const phraseTone = (phrase, i, n) => {
  // Small moves only: big pitch steps between utterances are what sounds
  // synthetic. Rate drifts a little per sentence so it is not metronomic.
  const t = { rate: 0.98 + ((i * 7) % 5) * 0.01, pitch: 1 };
  if (/\?$/.test(phrase)) t.pitch = 1.05; // question lifts
  else if (/^(sorry|couldn't|can't|failed|no )/i.test(phrase)) { t.rate -= 0.04; t.pitch = 0.96; }
  if (i === n - 1 && n > 1 && !/\?$/.test(phrase)) t.pitch -= 0.02; // settle at the end
  return t;
};

// Written for the eye, read for the ear: acronyms letter by letter, money
// and big numbers in words, markdown gone.
const ACRONYMS = { SaaS: "sass", VP: "V P", VPs: "V Ps", SDR: "S D R", SDRs: "S D Rs", CRM: "C R M", ICP: "I C P", US: "U S", UK: "U K", CEO: "C E O", CTO: "C T O", CFO: "C F O", GTM: "go to market", ARR: "A R R", MQL: "M Q L", SQL: "S Q L" };
export const forSpeech = text =>
  String(text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_`#>|]/g, "")
    .replace(/\$(\d+(?:\.\d+)?)([kKmM])\b/g, (m, n, u) => `${n} ${/k/i.test(u) ? "thousand" : "million"} dollars`)
    .replace(/\$(\d[\d,]*)/g, "$1 dollars")
    .replace(/\b(\d{1,3}),(\d{3}),(\d{3})\b/g, (m, a) => `about ${a} million`)
    .replace(/\b(\d{1,3}),(\d{3})\b/g, (m, a, b) => (Number(a) >= 10 ? `about ${a} thousand` : `${a} thousand ${Number(b) ? b.replace(/^0+/, "") : ""}`))
    .replace(/\b(SaaS|VPs?|SDRs?|CRM|ICP|US|UK|CEO|CTO|CFO|GTM|ARR|MQL|SQL)\b/g, m => ACRONYMS[m] || m)
    .replace(/\s+/g, " ")
    .trim();

let pending = 0; // utterances queued but not finished
// cancel() makes Chrome fire onend for the utterances it threw away, after
// the next ones are already queued; each generation ignores the last one's.
let generation = 0;
const utter = (phrase, tone) => {
  const u = new SpeechSynthesisUtterance(phrase);
  const v = resolveVoice();
  if (v) u.voice = v;
  Object.assign(u, tone);
  const gen = generation;
  pending += 1;
  if (!speaking) {
    speaking = u;
    notify("started");
  }
  u.onend = () => {
    if (gen !== generation) return;
    pending -= 1;
    if (pending <= 0) { pending = 0; speaking = null; notify("ended"); }
  };
  u.onerror = u.onend;
  window.speechSynthesis.speak(u);
};

// ── server voice: one fetch per sentence, played in order, next one
// fetched while the current plays. A failed fetch drops to the browser
// voice for the rest of the session.
let serverVoice = true;
const queue = []; // { text, audio }
let playing = null;
let playerGen = 0;

// The copilot token lives in memory (minted once by the transport); resolve
// it before the audio element gets its URL.
let credential = "";
export const withCredential = fn => {
  if (credential) { fn(credential); return; }
  ensureCopilotCredential().then(token => { credential = token; fn(token); });
};
export const ttsUrl = (text, token, voice = getPrefs().voice) =>
  `${agentBaseUrl()}/copilot/tts?text=${encodeURIComponent(text)}&voice=${encodeURIComponent(voice)}&key=${encodeURIComponent(token)}`;

const prime = item => {
  if (item.audio) return;
  const a = new Audio();
  a.preload = "auto";
  a.playbackRate = getPrefs().speed === "fast" ? 1.12 : 1;
  item.audio = a;
  withCredential(token => { if (item.audio === a) a.src = ttsUrl(item.text, token); });
};

const playNext = () => {
  const item = queue.shift();
  if (!item) {
    playing = null;
    notify("ended");
    return;
  }
  const gen = playerGen;
  prime(item);
  if (queue[0]) prime(queue[0]);
  const a = item.audio;
  playing = a;
  a.onended = () => { if (gen === playerGen) playNext(); };
  a.onerror = () => {
    if (gen !== playerGen) return;
    // Server voice is unavailable: finish this line in the browser voice.
    serverVoice = false;
    const rest = [item.text, ...queue.map(q => q.text)];
    queue.length = 0;
    playing = null;
    rest.forEach((t, i, all) => utter(t, phraseTone(t, i, all.length)));
  };
  withCredential(() => { if (gen === playerGen) a.play().catch(() => a.onerror()); });
};

const enqueueServer = phrases => {
  phrases.forEach(text => queue.push({ text, audio: null }));
  if (!playing) {
    notify("started");
    playNext();
  } else if (queue[0]) {
    prime(queue[0]);
  }
};

/** Add to what is being said; used while a reply streams in. */
export const enqueue = text => {
  if (!text || typeof window === "undefined") return;
  const line = forSpeech(text);
  if (!line) return;
  // One utterance per sentence; the engine phrases commas better than a
  // hard stop between utterances does.
  const phrases = (line.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g) || [line]).map(p => p.trim()).filter(Boolean);
  if (serverVoice) {
    enqueueServer(phrases);
    return;
  }
  if (!("speechSynthesis" in window)) return;
  phrases.forEach((phrase, i) => utter(phrase, phraseTone(phrase, i, phrases.length)));
};

export const speak = text => {
  stopSpeaking();
  enqueue(text);
};

export const isSpeaking = () => Boolean(speaking || playing);

export const stopSpeaking = () => {
  if (typeof window === "undefined") return;
  const was = speaking || playing;
  speaking = null;
  pending = 0;
  generation += 1;
  playerGen += 1;
  queue.length = 0;
  if (playing) {
    playing.pause();
    playing.src = "";
    playing = null;
  }
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  if (was) notify("ended");
};

/**
 * Continuous conversation. One toggle: while the session is on, the mic
 * listens, sends each utterance, stays quiet while the copilot is talking
 * (so it does not transcribe its own voice), and listens again when the
 * reply has been read. Stops on the toggle or when the tab loses the mic.
 */
let session = null;
export const voiceSessionOn = () => Boolean(session);

// Barge-in. Recognition is off while the copilot talks (it would transcribe
// itself), so a raw mic stream with echo cancellation watches for the user's
// voice instead; sustained energy cuts the speech and listening resumes.
let gate = null;
const startGate = onVoice => {
  if (gate || !navigator.mediaDevices) return;
  gate = { stopped: false };
  navigator.mediaDevices
    .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } })
    .then(stream => {
      if (!gate || gate.stopped) { stream.getTracks().forEach(t => t.stop()); return; }
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      const buf = new Uint8Array(an.fftSize);
      let hot = 0;
      const tick = () => {
        if (!gate || gate.stopped) return;
        an.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) { const d = (buf[i] - 128) / 128; sum += d * d; }
        const rms = Math.sqrt(sum / buf.length);
        // ponytail: fixed threshold; ~6 frames (~100ms) above it counts as speech.
        hot = rms > 0.06 ? hot + 1 : 0;
        if (hot >= 6) { onVoice(); return; }
        gate.raf = requestAnimationFrame(tick);
      };
      gate.cleanup = () => { cancelAnimationFrame(gate.raf); stream.getTracks().forEach(t => t.stop()); ctx.close().catch(() => {}); };
      tick();
    })
    .catch(() => { gate = null; });
};
const stopGate = () => {
  if (!gate) return;
  gate.stopped = true;
  if (gate.cleanup) gate.cleanup();
  gate = null;
};

export const startVoiceSession = ({ onInterim, onSend, onState }) => {
  if (session) return;
  // UI callbacks live on the session so a new composer (page -> drawer, or
  // a route change) can take them over without restarting the mic.
  session = { stop: null, paused: false, ui: { onInterim, onState }, onSend };
  const setState = st => { if (session && session.ui.onState) session.ui.onState(st); };
  const listenOnce = () => {
    if (!session || session.paused) return;
    setState("listening");
    session.stop = listen({
      onInterim: text => { if (session && session.ui.onInterim) session.ui.onInterim(text); },
      onFinal: text => { if (session) session.onSend(text); },
      onEnd: () => {
        if (!session) return;
        session.stop = null;
        // Recognition ends after every utterance or ~8s of silence; loop
        // unless we are paused for the reply.
        if (!session.paused) setTimeout(listenOnce, 150);
      }
    });
  };
  const unsub = onSpeech(state => {
    if (!session) return;
    if (state === "started") {
      session.paused = true;
      if (session.stop) session.stop();
      setState("speaking");
      if (getPrefs().bargeIn) startGate(() => { stopGate(); stopSpeaking(); });
    } else if (state === "ended") {
      stopGate();
      session.paused = false;
      setTimeout(listenOnce, 200);
    }
  });
  session.unsub = unsub;
  listenOnce();
};

/** A newly mounted composer adopts the running session's UI hooks. */
export const attachVoiceUi = ({ onInterim, onState }) => {
  if (!session) return false;
  session.ui = { onInterim, onState };
  if (onState) onState(session.paused ? "speaking" : "listening");
  return true;
};

/** Detach without stopping: the composer is going away, the session is not. */
export const detachVoiceUi = () => {
  if (session) session.ui = {};
};

export const stopVoiceSession = () => {
  if (!session) return;
  const s = session;
  session = null;
  if (s.stop) s.stop();
  if (s.unsub) s.unsub();
  stopGate();
  stopSpeaking();
};

/**
 * Streaming narration. Sentences are spoken as the text block completes
 * them, a step label fills the silence while a tool runs, and turnEnd
 * says whatever is left plus the open question. Only in talk mode.
 */
const turns = new Map(); // turnId -> { spokenChars, said }
const SENTENCE_END = /[.!?](\s|$)/g;
export const narrator = {
  start(turnId) {
    if (!session || getPrefs().readAloud === "never") return;
    turns.set(turnId, { spokenChars: 0, said: false });
  },
  // Text so far for this turn's text block; speak the newly completed sentences.
  text(turnId, markdown) {
    const t = turns.get(turnId);
    if (!t || !session) return;
    let last = t.spokenChars;
    SENTENCE_END.lastIndex = 0;
    let m;
    while ((m = SENTENCE_END.exec(markdown))) last = m.index + 1;
    if (last <= t.spokenChars) return;
    const chunk = markdown.slice(t.spokenChars, last);
    t.spokenChars = last;
    if (!t.said) {
      // First real words: cut any filler still playing.
      stopSpeaking();
      t.said = true;
    }
    enqueue(chunk);
  },
  // A tool started: say what, once, so the pause is not dead air.
  step(turnId, label) {
    const t = turns.get(turnId);
    if (!t || !session || t.said || isSpeaking() || !label) return;
    enqueue(`${label.replace(/\.+$/, "")}.`);
  },
  end(turnId, message) {
    const t = turns.get(turnId);
    turns.delete(turnId);
    if (!session) return;
    const blocks = (message && message.blocks) || [];
    const text = blocks.find(b => b.type === "text" && b.markdown);
    if (t && text) {
      const rest = text.markdown.slice(t.spokenChars).trim();
      // Tail without terminal punctuation, and anything we never got to.
      if (rest && rest.length > 2) enqueue(rest);
      // A table-only turn: say the count.
      if (!t.said && !rest) enqueue(spokenLineFor(message));
    } else if (!t || !t.said) {
      enqueue(spokenLineFor(message));
      return;
    }
    // The question or choice the turn is waiting on, always.
    const parts = [];
    const strip = x => String(x || "").replace(/[*_`#>]/g, "").trim();
    const form = blocks.find(b => b.type === "question_form" && !b.done);
    if (form && form.questions && form.questions[0]) parts.push(strip(form.questions[0].question));
    const choices = blocks.find(b => b.type === "quick_replies" && !b.selectedValue);
    if (choices && choices.prompt) parts.push(strip(choices.prompt));
    const error = blocks.find(b => b.type === "error_cta");
    if (error && error.title) parts.unshift(strip(error.title));
    const extra = parts.join(" ");
    if (extra && !(text && text.markdown.includes(extra))) enqueue(extra);
  }
};

/**
 * What to read aloud for a finished assistant turn. No model call: the
 * first prose sentence or two, stripped of markdown, plus the question when
 * the turn is waiting on the user. Tables and cards are for the eyes.
 */
export const spokenLineFor = message => {
  const blocks = (message && message.blocks) || [];
  const strip = s =>
    String(s || "")
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/[*_`#>]/g, "")
      .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
      .replace(/\s+/g, " ")
      .trim();
  const parts = [];
  const text = blocks.find(b => b.type === "text" && strip(b.markdown));
  if (text) {
    const sentences = strip(text.markdown).match(/[^.!?]+[.!?]?/g) || [];
    parts.push(sentences.slice(0, 2).join(" ").trim());
  } else {
    const table = blocks.find(b => b.type === "table_ref");
    if (table && table.totalCount) parts.push(`Found ${Number(table.totalCount).toLocaleString()} matches.`);
  }
  const form = blocks.find(b => b.type === "question_form" && !b.done);
  if (form && form.questions && form.questions[0]) parts.push(strip(form.questions[0].question));
  const choices = blocks.find(b => b.type === "quick_replies" && !b.selectedValue);
  if (choices && choices.prompt) parts.push(strip(choices.prompt));
  const error = blocks.find(b => b.type === "error_cta");
  if (error && error.title) parts.unshift(strip(error.title));
  return parts.filter(Boolean).join(" ").slice(0, 320);
};
